<template>
  <div
    v-if="content !== null"
    class="home-hero"
  >
    <div
      class="home-hero__full-size-image-wrapper"
      v-show="content.FSTwave !== null"
    >
      <ResponsiveImage :picture="content.Image" />
      <div class="home-hero__full-size-image-wrapper__wave pure-u-1">
        <WaveWhite
          class="w-full"
          v-show="content.FSTwave"
          :alt="$tv('waveAltText', 'home')"
        />
      </div>
    </div>
    <div class="home-hero__search-panel">
      <div
        class="home-hero__search-panel__section home-hero__search-panel__section--search"
        :class="{'home-hero__search-panel__section--category-selected': activeCategory}"
      >
        <div class="home-hero__search-panel__heading-row">
          <h2>{{ $tv('detailSearch', 'search', 'Advanced Search') }}</h2>
          <ToggleSwitchSquare
            class="home-hero__search-panel__toggle"
            :label-right="$tv('metricInchToggleRightText','product')"
            :label-left="$tv('metricInchToggleLeftText','product')"
            :is-checked="showInchValues"
            @change-value="toggleShowInchValues"
          />
        </div>

        <div class="home-hero__search-panel__controls">
          <div
            class="home-hero__search-panel__filters"
            :class="{'home-hero__search-panel__filters--placeholder': !areActiveCategoryFiltersReady && placeholderCategoryTiles.length}"
          >
            <div class="home-hero__search-panel__field">
              <label for="home-hero-category-search">{{ $tv('categories', 'search') }}</label>
              <ComboDropDown
                id="home-hero-category-search"
                :data-items="categoryDropdownItems"
                v-model="selectedCategoryDropdownValue"
                text-field="label"
                data-item-key="value"
                :filterable="true"
                :clear-button="false"
                :placeholder="$tv('selectCategory', 'search')"
                :popup-class-name="'home-hero__search-panel__dropdown-popup'"
                class="home-hero__search-panel__category-autocomplete"
              />
            </div>

            <template v-if="areActiveCategoryFiltersReady">
              <div
                v-for="filter in activeCategoryFilters"
                :key="filter.technicalName"
                class="home-hero__search-panel__filter"
              >
                <label
                  :for="`home-hero-filter-${filter.technicalName}`"
                  v-html="filter.displayName"
                />
                <DropDown
                  v-if="isTypeDropdown(filter.type)"
                  :id="`home-hero-filter-${filter.technicalName}`"
                  :data-items="getFilterDropdownItems(filter)"
                  :value="selectedFilterDropdownValue(filter)"
                  :text-field="'label'"
                  :data-item-key="'value'"
                  :disabled="!activeCategory"
                  :popup-class-name="'home-hero__search-panel__dropdown-popup'"
                  @change="updateFilterDropdownValue(filter, $event.value)"
                />
                <MultiSelectDropDown
                  v-else-if="isTypeString(filter.type) || isTypeMultiInteger(filter.type) || isTypeMultiString(filter.type)"
                  :id="`home-hero-filter-${filter.technicalName}`"
                  :data-items="getFilterMultiSelectItems(filter)"
                  :value="selectedMultiSelectDropdownValue(filter)"
                  :text-field="'label'"
                  :data-item-key="'value'"
                  :filterable="true"
                  :disabled="!activeCategory"
                  :popup-class-name="'home-hero__search-panel__dropdown-popup'"
                  @change="updateFilterMultiSelectValue(filter, $event.value)"
                />
                <NumericInput
                  v-else-if="isTypeNumber(filter.type) || isTypeMultiDouble(filter.type) || isTypeMinMaxNumber(filter.type)"
                  :id="`home-hero-filter-${filter.technicalName}`"
                  :model-value="getNumericInputValue(filter)"
                  :format="getNumericInputFormat(filter.type)"
                  @change="handleNumericInputChange(filter, $event)"
                />
                <ToggleSwitch
                  v-else-if="isTypeBool(filter.type)"
                  :is-checked="Boolean(selectedFilters[filter.technicalName])"
                  :is-disabled="!activeCategory"
                  @change-value="updateFilterValue(filter, $event)"
                />
              </div>
            </template>

            <template v-else>
              <div
                v-for="index in placeholderFilterCount"
                :key="`placeholder-filter-${index}`"
                class="home-hero__search-panel__filter home-hero__search-panel__filter--placeholder"
              >
                <label>&nbsp;</label>
                <input
                  type="text"
                  disabled
                  aria-hidden="true"
                >
              </div>
            </template>
          </div>

          <div class="home-hero__search-panel__actions">
            <KendoButton
              class="home-hero__search-panel__search-button"
              :theme-color="'base'"
              :disabled="!canSearch"
              @click="handleSearch"
            >
              {{ $tv('users.find', 'management') }}
            </KendoButton>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="content.LeftText"
      class="home-hero__left-text"
      :class="{'home-hero__left-text--light': content.TextAppearance === 'Light'}"
    >
      <p v-text="content.LeftText" />
    </div>
    <div
      class="home-hero__text"
      :class="{
        'home-hero__text--light': content.TextAppearance === 'Light',
        'home-hero__text--corteco': content.LeftText
      }"
    >
      <div
        v-if="content.Subline"
        class="home-hero__text__subline"
        v-text="content.Subline"
      />
      <div>
        <h1
          v-if="content.Header"
          class="h1 h1--underline"
          v-text="content.Header"
        />
      </div>
      <div
        class="home-hero__text__btn-container"
        :class="{'home-hero__text__btn-container--non-english': content.isFNLubeBanner && content.IsNonEnglish}"
      >
        <a
          v-if="content.Link"
          :href="content.Link"
          class="home-hero__text__btn-container__btn k-button k-button-md k-button-solid k-button-solid-primary"
        >
          <span v-text="buttonText" />
        </a>
        <a
          v-if="content.SecondaryLink"
          :href="content.SecondaryLink"
          :class="content.isFNLubeBanner ? 'home-hero__text__btn-container__text-link' : 'home-hero__text__btn-container__btn k-button k-button-md k-button-solid k-button-solid-base'"
        >
          <span v-text="secondaryButtonText" />
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import ResponsiveImage from '~/components/responsive-image'
import WaveWhite from '~/assets/images/wave-white.svg?component'
import DropDown from '~/components/input-components/DropDown'
import ComboDropDown from '~/components/input-components/ComboDropDown.vue'
import MultiSelectDropDown from '~/components/input-components/MultiSelectDropDown'
import NumericInput from '~/components/input-components/NumericInput'
import ToggleSwitch from '~/components/input-components/ToggleSwitch.vue'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import {Button as KendoButton} from '@progress/kendo-vue-buttons'
import {VALUE_TYPES, ATTRIBUTE_IDENTIFIER_NEW, cleanupFacets, cleanupFilters} from '~/assets/js/filters'
import {FACET_VALUE_TYPES, PROPERTY_SCOPE_NAMES, REGION_KEYS} from '~/assets/js/constants'
import {mapProductAttributeNames} from '~/assets/js/utils/product_attributes_mapping'
import {usePimHelper} from '~/composables/pim_helper'
import {useProductService} from '~/composables/product_service'
import {useCategoryContentsService} from '~/composables/category_contents_service'
import {useUserStore} from '~/store/user'
import {useEventItemConverter} from '@/composables/useEventItemConverter'
import {useGtm} from '@gtm-support/vue-gtm'
import {storeToRefs} from 'pinia'
import {useCategoriesMenuStore} from '@/store/categories-menu'

export default {
  components: {ResponsiveImage, WaveWhite, DropDown, ComboDropDown, MultiSelectDropDown, NumericInput, KendoButton, ToggleSwitch, ToggleSwitchSquare},
  setup () {
    const {mapCategoryFilter, loadProductCategoryWithPim} = usePimHelper()
    const productService = useProductService()
    const {loadStartpageCategories} = useCategoryContentsService()
    const userStore = useUserStore()
    const {showInchValues} = storeToRefs(userStore)
    const {setShowInchValues} = userStore
    const categoriesMenuStore = useCategoriesMenuStore()
    return {
      mapCategoryFilter,
      loadProductCategoryWithPim,
      productService,
      loadStartpageCategories,
      userStore,
      showInchValues,
      setShowInchValues,
      categoriesMenuStore
    }
  },
  props: {
    content: {
      type: Object,
      default () {
        return null
      }
    }
  },
  data() {
    return {
      BASE_URL: useRuntimeConfig().public.BASE_URL || '',
      categories: [],
      categorySearch: '',
      selectedCategory: null,
      selectedFilters: {},
      showCategoryList: false,
      isLoadingCategories: false,
      pimCategoryFilters: [],
      pimCategoryFacets: {},
      pimCategoryTechnicalName: null,
      pimCategoryUrl: null,
      isLoadingPimFilters: false
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.loadCategories()
      }
    }
  },
  computed: {
    buttonText () {
      if (this.content?.ButtonLabel) {
        return this.content.ButtonLabel
      }
      return this.$tv('learnMore', 'home')
    },
    secondaryButtonText () {
      if (this.content?.SecondaryButtonLabel) {
        return this.content.SecondaryButtonLabel
      }
      return this.$tv('showAllProducts', 'home')
    },
    measurementUnit () {
      return this.showInchValues ? 'inch' : 'mm'
    },
    categoryDropdownItems () {
      return this.categories.map((option) => ({
        label: this.getCategoryDisplayName(option),
        value: option.technicalName,
        category: option
      }))
    },
    selectedCategoryDropdownValue: {
      get () {
        if (!this.activeCategory) {
          return null
        }
        return this.categoryDropdownItems.find((item) => item.value === this.activeCategory.technicalName) || null
      },
      set (selectedValue) {
        this.selectCategory(selectedValue?.category || null)
      }
    },
    defaultCategoryOption () {
      return {label: this.$tv('selectCategory', 'search'), value: ''}
    },
    activeCategory () {
      return this.selectedCategory
    },
    areActiveCategoryFiltersReady () {
      return Boolean(
        this.activeCategory &&
        !this.isLoadingPimFilters &&
        this.pimCategoryTechnicalName === this.activeCategory.technicalName
      )
    },
    activeCategoryFilters () {
      return (this.activeCategory?.filters || [])
        .filter((filter) => filter.technicalName)
        .map((filter) => {
          const pimFilter = this.findPimFilter(filter.technicalName)
          const rawUnit = pimFilter?.Attribute?.UnitOfMeasure ?? pimFilter?.unitOfMeasure ?? filter.unitOfMeasure
          const displayUnitKey = rawUnit && rawUnit.toLowerCase() === 'mm' && this.measurementUnit === 'inch'
            ? 'inch'
            : rawUnit?.toLowerCase()
          const displayUnit = displayUnitKey
            ? this.$tv(displayUnitKey, 'unitsOfMeasure', displayUnitKey)
            : rawUnit
          const type = pimFilter?.type || pimFilter?.Attribute?.ValueType
          return {
            ...filter,
            type,
            displayName: this.buildFilterLabel(pimFilter, filter, displayUnit),
            sourceUnitOfMeasure: rawUnit,
            unitOfMeasure: displayUnit
          }
        })
    },
    canSearch () {
      const filledFilters = Object.values(this.selectedFilters).filter((value) => {
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return value !== null && value !== undefined && value !== ''
      })

      return Boolean(this.activeCategory && filledFilters.length >= 1)
    },
    topCategoryOptions () {
      return this.categories.slice(0, 6)
    },
    placeholderCategoryTiles () {
      return this.categories.slice(0, 4)
    },
    placeholderFilterCount () {
      return 4
    }
  },
  mounted () {
    const region = this.$globalization?.getRegion?.() || ''
    if (region.toLowerCase() === REGION_KEYS.NA.toLowerCase() && !this.showInchValues && !this.userStore?.loggedIn) {
      this.setShowInchValues(true)
    }
    this.loadCategories()
  },
  methods: {
    async loadCategories () {
      this.isLoadingCategories = true

      try {
        const region = this.$globalization?.getRegion?.() || 'global'
        this.categories = await this.loadStartpageCategories(region)
      } catch (error) {
        this.categories = []
      } finally {
        this.isLoadingCategories = false
      }
    },
    getCategoryDisplayName (option) {
      if (!option) {
        return ''
      }
      const technicalName = typeof option === 'string' ? option : option.technicalName
      const menuCategory = this.categoriesMenuStore.findByTechnicalName(technicalName)
      return menuCategory?.displayName || menuCategory?.shortName || option?.displayName || (technicalName ? this.$tv(technicalName, 'categories', technicalName) : '')
    },
    selectCategory (option) {
      if (!option) {
        this.selectedCategory = null
        this.categorySearch = ''
        this.selectedFilters = {}
        this.showCategoryList = false
        this.pimCategoryFilters = []
        this.pimCategoryFacets = {}
        this.pimCategoryTechnicalName = null
        this.pimCategoryUrl = null
        return
      }

      this.selectedCategory = option
      this.categorySearch = this.getCategoryDisplayName(option)
      this.selectedFilters = {}
      this.showCategoryList = false
      this.loadPimCategoryFilters(option.technicalName)
    },
    async loadPimCategoryFilters (technicalName) {
      if (!technicalName) {
        this.pimCategoryFilters = []
        this.pimCategoryFacets = {}
        this.pimCategoryTechnicalName = null
        return
      }
      this.isLoadingPimFilters = true
      try {
        const rawCategoryJson = await this.loadProductCategoryWithPim(technicalName, this.$route)
        const categoryJson = typeof rawCategoryJson === 'string' ? JSON.parse(rawCategoryJson) : rawCategoryJson
        if (!categoryJson) {
          return
        }

        const rawFilters = [
          ...(categoryJson?.categoryAttributes_filter_core?.map(f => this.mapCategoryFilter(f, true)) || []),
          ...(categoryJson?.categoryAttributes_filter?.map(f => this.mapCategoryFilter(f, false)) || [])
        ]
        const cleanedFilters = cleanupFilters(rawFilters)

        const facetKeys = rawFilters
          .filter(cat => cat.Attribute?.IndexPath && cat.Attribute?.IsFilterable && FACET_VALUE_TYPES.includes(cat.Attribute.ValueType))
          .map(cat => cat.Attribute.IndexPath)
        const cleanedFacets = cleanupFacets(facetKeys)

        const designs = (categoryJson.designTechnicalNames || []).map(d => ({TechnicalName: d}))
        const locale = this.$i18n?.locale?.value ?? this.$i18n?.locale

        let facetValues = {}
        if (designs.length && cleanedFacets.length) {
          const result = await this.productService.getProductsForDesignsNew(
            designs,
            cleanedFacets,
            this.$globalization.getRegion(),
            locale,
            [],
            {sort: [], pagination: {skip: 0, take: 1}},
            null,
            PROPERTY_SCOPE_NAMES.CATEGORY_PAGE_NEW,
            rawFilters.map(f => f.IndexPath)
          )
          facetValues = result?.facets || {}
        }

        if (this.selectedCategory?.technicalName === technicalName) {
          this.pimCategoryFilters = cleanedFilters
          this.pimCategoryFacets = facetValues
          this.pimCategoryTechnicalName = technicalName
          this.pimCategoryUrl = categoryJson.url || null
        }
      } catch (error) {
        this.pimCategoryFilters = []
        this.pimCategoryFacets = {}
        this.pimCategoryTechnicalName = null
        this.pimCategoryUrl = null
      } finally {
        this.isLoadingPimFilters = false
      }
    },
    findPimFilter (technicalName) {
      if (!technicalName || !this.pimCategoryFilters.length) {
        return null
      }
      const target = technicalName.toLowerCase()
      return this.pimCategoryFilters.find((f) => (f?.shortName || '').toLowerCase() === target) || null
    },
    buildFilterLabel (pimFilter, filter, unitOfMeasure) {
      const name = pimFilter?.displayName || pimFilter?.Label || filter.displayName || (filter.technicalName ? this.$tv(filter.technicalName, 'attributes', filter.technicalName) : '')
      const short = pimFilter?.ShortLabel || pimFilter?.displayNameShort
      let parts = [name]
      if (short && short !== name) {
        parts = [...parts, `(${short})`]
      }
      if (unitOfMeasure) {
        parts = [...parts, `[${unitOfMeasure}]`]
      }
      return parts.join(' ')
    },
    getFacetKey (pimFilter, filter) {
      const shortName = pimFilter?.Attribute?.TechnicalName || pimFilter?.ShortLabel || pimFilter?.shortName || filter.technicalName
      let prop = pimFilter?.Attribute?.IndexPath || pimFilter?.indexPath
        ? mapProductAttributeNames(pimFilter.Attribute?.IndexPath || pimFilter.indexPath)
        : ATTRIBUTE_IDENTIFIER_NEW + shortName
      if (prop.startsWith('characteristics/')) {
        prop = ATTRIBUTE_IDENTIFIER_NEW + prop.substring('characteristics/'.length)
      }
      return prop
    },
    getFilterDropdownItems (filter) {
      const selectedValue = this.selectedFilters[filter.technicalName]
      const pimFilter = this.findPimFilter(filter.technicalName)
      const translatePrefix = `attributes.${filter.technicalName}-values`

      const facetKey = this.getFacetKey(pimFilter, filter)
      const facets = facetKey ? this.pimCategoryFacets?.[facetKey] : null

      if (Array.isArray(facets) && facets.length > 0) {
        return facets.map((facet) => {
          const raw = facet?.value ?? facet
          const translated = this.$tv(String(raw), translatePrefix)
          return {value: raw, label: translated || String(raw)}
        })
      }

      return selectedValue ? [{label: selectedValue, value: selectedValue}] : [{label: 'Select value', value: ''}]
    },
    selectedFilterDropdownValue (filter) {
      const selectedValue = this.selectedFilters[filter.technicalName]
      if (!selectedValue) {
        return null
      }

      return {label: selectedValue, value: selectedValue}
    },
    updateFilterDropdownValue (filter, value) {
      const nextValue = value?.value ?? value ?? ''
      this.updateFilterValue(filter, nextValue)
    },
    getFilterMultiSelectItems (filter) {
      const pimFilter = this.findPimFilter(filter.technicalName)
      const translatePrefix = `attributes.${filter.technicalName}-values`

      const facetKey = this.getFacetKey(pimFilter, filter)
      const facets = facetKey ? this.pimCategoryFacets?.[facetKey] : null

      if (Array.isArray(facets) && facets.length > 0) {
        return facets.map((facet) => {
          const raw = facet?.value ?? facet
          const translated = this.$tv(String(raw), translatePrefix)
          return {value: raw, label: translated || String(raw)}
        })
      }

      const selected = this.selectedFilters[filter.technicalName]
      if (Array.isArray(selected) && selected.length > 0) {
        return selected.map((val) => typeof val === 'object' && val !== null ? val : {value: val, label: String(val)})
      }

      return []
    },
    selectedMultiSelectDropdownValue (filter) {
      const selected = this.selectedFilters[filter.technicalName]
      if (!selected) {
        return []
      }
      if (Array.isArray(selected)) {
        return selected.map((val) => {
          if (typeof val === 'object' && val !== null) {
            return val
          }
          const items = this.getFilterMultiSelectItems(filter)
          const matched = items.find((item) => item.value === val)
          return matched || {value: val, label: String(val)}
        })
      }
      return [{value: selected, label: String(selected)}]
    },
    updateFilterMultiSelectValue (filter, value) {
      const nextValue = Array.isArray(value) ? value.map((v) => v?.value ?? v) : (value?.value ?? value ?? [])
      this.updateFilterValue(filter, nextValue)
    },
    getNumericInputFormat (type) {
      if (type === VALUE_TYPES.FLOAT || type === VALUE_TYPES.MULTI_DOUBLE) {
        return this.showInchValues ? 'n3' : 'n2'
      }
      return 'n0'
    },
    isMillimeterFilter (filter) {
      const pimFilter = this.findPimFilter(filter.technicalName)
      const unitOfMeasure = pimFilter?.Attribute?.UnitOfMeasure ?? pimFilter?.unitOfMeasure ?? filter.sourceUnitOfMeasure ?? filter.unitOfMeasure
      return unitOfMeasure?.toLowerCase() === 'mm'
    },
    getNumericInputValue (filter) {
      return this.selectedFilters[filter.technicalName] ?? ''
    },
    handleNumericInputChange (filter, value) {
      this.updateFilterValue(filter, value)
    },
    isTypeDropdown (type) {
      return type === VALUE_TYPES.DROPDOWN
    },
    isTypeString (type) {
      return type === VALUE_TYPES.STRING
    },
    isTypeMultiString (type) {
      return type === VALUE_TYPES.MULTI_STRING
    },
    isTypeMultiInteger (type) {
      return type === VALUE_TYPES.MULTI_INTEGER
    },
    isTypeMultiDouble (type) {
      return type === VALUE_TYPES.MULTI_DOUBLE
    },
    isTypeNumber (type) {
      return type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT
    },
    isTypeMinMaxNumber (type) {
      return type === VALUE_TYPES.MIN_FLOAT || type === VALUE_TYPES.MAX_FLOAT
    },
    isTypeBool (type) {
      return type === VALUE_TYPES.BOOLEAN
    },
    updateFilterValue (filter, value) {
      this.selectedFilters = {
        ...this.selectedFilters,
        [filter.technicalName]: value
      }
    },
    toggleShowInchValues (value) {
      const nextVal = typeof value === 'boolean' ? value : !this.showInchValues
      const convertedFilters = {...this.selectedFilters}

      this.activeCategoryFilters.forEach((filter) => {
        const currentValue = convertedFilters[filter.technicalName]
        const isNumericFilter = this.isTypeNumber(filter.type) || this.isTypeMultiDouble(filter.type) || this.isTypeMinMaxNumber(filter.type)
        if (!isNumericFilter || !this.isMillimeterFilter(filter) || currentValue === null || currentValue === undefined || currentValue === '') {
          return
        }

        const numericValue = Number(currentValue)
        if (Number.isFinite(numericValue)) {
          const convertedValue = nextVal ? numericValue / 25.4 : numericValue * 25.4
          convertedFilters[filter.technicalName] = Number.parseFloat(convertedValue.toFixed(4))
        }
      })

      this.selectedFilters = convertedFilters
      this.setShowInchValues(nextVal)
    },
    trackFacetedSearch () {
      const props = {
        category: this.activeCategory.technicalName
      }
      this.activeCategoryFilters
        .filter((filter) => {
          const value = this.selectedFilters[filter.technicalName]
          return Array.isArray(value)
            ? value.length > 0
            : value !== null && value !== undefined && value !== ''
        })
        .forEach((filter) => {
          const value = this.selectedFilters[filter.technicalName]
          const isNumericFilter = this.isTypeNumber(filter.type) || this.isTypeMultiDouble(filter.type) || this.isTypeMinMaxNumber(filter.type)
          const unitOfMeasure = isNumericFilter ? filter.unitOfMeasure : null
          props[filter.technicalName] = Array.isArray(value)
            ? value.join(', ')
            : unitOfMeasure
              ? `${value} ${unitOfMeasure}`
              : value
        })

      useGtm()?.trackEvent(useEventItemConverter().convert({
        event: 'easy_faceted_search_submitted',
        add_personalization: true,
        props
      }))
    },
    handleSearch () {
      if (!this.activeCategory || !this.canSearch) {
        return
      }

      this.trackFacetedSearch()

      const query = this.buildSearchQuery()
      const targetPath = this.pimCategoryUrl
        ? (this.pimCategoryUrl.startsWith('/') ? this.pimCategoryUrl : `/${this.pimCategoryUrl}`)
        : null

      if (!targetPath) {
        // eslint-disable-next-line no-console
        console.warn('[HomeHero] No category URL available for navigation')
        return
      }
      this.setShowInchValues(this.showInchValues)
      this.$router.push({path: targetPath, query, hash: '#resultFilterSection'})
    },
    buildSearchQuery () {
      const query = {
        order_by: 'productRank',
        order: 'asc'
      }
      Object.entries(this.selectedFilters).forEach(([technicalName, value]) => {
        if (value === null || value === undefined || value === '') {
          return
        }
        const pimFilter = this.findPimFilter(technicalName)
        const activeFilter = this.activeCategoryFilters.find((filter) => filter.technicalName === technicalName)
        const type = pimFilter?.Attribute?.ValueType || pimFilter?.type || activeFilter?.type
        const isNumericFilter = type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT || type === VALUE_TYPES.MULTI_DOUBLE || type === VALUE_TYPES.MIN_FLOAT || type === VALUE_TYPES.MAX_FLOAT
        let queryValue = value
        if (this.showInchValues && activeFilter && this.isMillimeterFilter(activeFilter) && isNumericFilter) {
          const numericValue = Number(value)
          if (Number.isFinite(numericValue)) {
            queryValue = Number.parseFloat((numericValue * 25.4).toFixed(4))
          }
        }

        if (type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT || type === VALUE_TYPES.MULTI_DOUBLE) {
          query[technicalName] = `${queryValue}~2`
        } else if (type === VALUE_TYPES.MIN_FLOAT || type === VALUE_TYPES.MAX_FLOAT) {
          const suffix = type === VALUE_TYPES.MIN_FLOAT ? '_min' : '_max'
          query[technicalName + suffix] = String(queryValue)
        } else if (Array.isArray(value)) {
          if (value.length > 0) {
            query[technicalName] = value.map((v) => v?.value ?? v).join('$')
          }
        } else {
          query[technicalName] = String(value)
        }
      })
      return query
    }
  }
}
</script>

<style lang="scss">
  .home-hero {
    position: relative;

    &__full-size-image-wrapper {
      position: relative;

      picture {
        display: block;
      }

      svg.icon--svg {
        width: 100%;
      }

      &__wave {
        position: absolute;
        bottom: 0;

        img {
          max-width: 100%;
        }

        @include breakpoint-down($md) {
          display: none;
        }
      }
    }

    &__search-panel {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      width: 100%;
      box-sizing: border-box;
      gap: 1rem;
      margin-top: -0.25rem;
      padding: 1rem 2rem 1.5rem;
      background: $color__primary;
      box-shadow: 0 0.25rem 1rem rgb(0 0 0 / 8%);

      .toggle-switch-square {
        &__button-left,
        &__button-right {
          &:not(.is-not-checked) {
            border: 0.0625rem solid $color__white !important;
            box-sizing: border-box;
          }
        }
      }

      @include breakpoint-down($md) {
        margin: 1rem 0 2rem;
        padding: 1rem;
      }

      &__section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        width: 100%;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          right: 0.5rem;
          background: linear-gradient(to left, rgba($color__primary, 0.99) 0%, transparent 80%);
          pointer-events: none;
          z-index: 1;

          @include breakpoint-down($sm) {
            top: 8rem;
            right: 0;
          }
        }

        &--category-selected::before {
          display: none;
        }

        @include breakpoint-down($md) {
          gap: 0.75rem;
        }
      }

      &__heading-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
        z-index: 2;
        color: $color__white;

        .toggle-switch-square {
          justify-content: flex-end;
        }

        h2,
        h3 {
          margin: 0;
          color: $color__white;
        }
      }

      &__toggle {
        min-width: 8rem;
      }

      &__controls {
        position: relative;
        display: flex;
        flex-flow: row wrap;
        align-items: flex-end;
        color: $color__white;
        gap: 1rem;
      }

      &__field {
        display: flex;
        flex-flow: column;
        gap: 0.375rem;
        flex: 0 1 11.875rem;
        min-width: 0;

        label {
          margin: 0;
          font-weight: $base-text-font-weight-light;
        }

        .dropdown-wrapper {
          width: 100%;
          min-width: 0;
          max-width: 11.875rem;
        }

        .k-dropdown,
        .k-combobox {
          width: 100%;
        }

        @include breakpoint-down($sm) {
          flex-basis: 100%;

          .dropdown-wrapper {
            max-width: none;
          }
        }
      }

      &__category-autocomplete {
        .k-input-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: $color__fst_gray__100;
          cursor: pointer;

          .k-icon,
          .k-button-icon,
          .k-svg-icon {
            min-height: auto;
            width: 2rem;
            color: $color__fst_gray__100;
            background-color: transparent;
            padding: 0.5rem;
          }
        }

        .k-input-solid {
          border: 0.0625rem solid $color--input__border;
          border-radius: 0;
          box-sizing: border-box;
          background: $color__white;
          max-height: 2.5rem;
        }

        .k-input-inner {
          padding: 0.75rem 1rem;
          height: 2.5rem;
          box-sizing: border-box;
        }

        .k-input > .k-input-inner:focus-visible {
          border: 0 !important;
          box-shadow: none;
          outline: none ;
        }

        .k-input-solid:focus,
        .k-input-solid.k-focus,
        .k-input-solid.k-state-focused {
          border: 0.0625rem solid $color--input__border;
          box-shadow: none;
          outline: none;
        }
      }

      &__category-list {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        max-height: 10rem;
        overflow-y: auto;
        padding-right: 0.25rem;
      }

      &__category-option,
      &__top-category,
      &__all-categories,
      &__search-button {
        border: 0.0625rem solid $color__container__border;
        background: $color__white;
        color: $color__primary;
        padding: 0.6rem 0.8rem;
        text-align: left;
        cursor: pointer;
      }

      &__top-categories {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      &__all-categories {
        background: $color__white;
        color: $color__primary;
      }

      &__search-button {
        min-width: 10rem;
        align-self: end;
        margin-top: auto;
        background: $color--btn__tertiary !important;
      }

      &__filters {
        display: flex;
        flex-flow: row wrap;
        align-items: flex-end;
        gap: 1rem;
        flex: 1 1 20rem;
        min-width: 0;

        &--placeholder::before {
          content: '';
          position: absolute;
          inset: 0;
          top: -4.0625rem;
          bottom: -1.5rem;
          right: -1.5rem;
          background: linear-gradient(to left, rgba($color__primary, 0.95) 0%, transparent 90%);
          pointer-events: none;
          z-index: 1;

          @include breakpoint-down($md) {
            top: -3.0625rem;
            bottom: 0;
            right: 0;
          }

          @include breakpoint-down($sm) {
            top: 3.9375rem;
            bottom: 2.75rem;
            right: 0;
          }
        }
      }

      &__filter {
        display: flex;
        flex-flow: column nowrap;
        gap: 0.375rem;
        flex: 1 1 11.875rem;
        min-width: 0;
        max-width: 11.875rem;

        label {
          flex: 0 0 auto;
          min-width: 0;
          margin: 0;
          font-weight: $base-text-font-weight-light;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        input {
          width: 100%;
          min-width: 0;
          max-width: 11.875rem;
          height: 2.5rem;
          border: 0.0625rem solid $color__container__border;
          padding: 0.6rem 0.75rem;
          box-sizing: border-box;
        }

        .dropdown-wrapper {
          flex: 1 1 0;
          width: 100%;
          min-width: 0;
          max-width: 11.875rem;
        }

        .k-dropdown {
          width: 100%;
        }

        @include breakpoint-down($sm) {
          flex-basis: 100%;
          max-width: none;

          input,
          .dropdown-wrapper {
            max-width: none;
          }
        }

        &--placeholder {
          input {
            background: $color__white;
            cursor: not-allowed;
          }

          &:last-child {
          &::before {
            content: '';
              position: absolute;
              inset: 0;
              right: 11.5rem;
              background: linear-gradient(to left, rgba($color__primary, 0.96) 0%, transparent 60%);
              pointer-events: none;
              z-index: 1;

              @include breakpoint-down($md) {
                inset: unset;
              }
            }
          }
        }
      }

      &__hint {
        font-size: 0.8rem;
        color: $color__text__default;
      }

      &__actions {
        display: flex;
        justify-content: flex-end;
        margin-left: auto;
        flex: 0 0 auto;
        z-index: 2;

        .k-button.k-button-md {
          min-width: 11.5rem;
        }

        @include breakpoint-down($md) {
          margin-left: 0;
          width: 100%;
          justify-content: flex-start;

          .k-button.k-button-md {
            width: 100%;
            min-width: 0;
          }
        }
      }
    }

    &__left-text {
      color: $color__white;
      font-size: 0.875rem;
      line-height: 1.138rem;
      position: absolute;
      bottom: 1.688em;
      left: 5rem;
      width: 16rem;

      @include breakpoint-down($between_md_lg) {
        display: none;
      }

      p {
        margin: 0;
        font-weight: $base-text-font-weight-semi-bold;
      }
    }

    &__text {
      width: calc(100% - 2rem);
      color: $color__primary;

      @include breakpoint-up($between_md_lg) {
        position: absolute;
        top: .625rem;
        right: 2rem;
        width: 30rem;
      }

      @include breakpoint-down($between_md_lg) {
        font-size: $base-text-font-size;
        padding: $default-margin-padding-width;
      }

      @include breakpoint-between($md, $between_md_lg) {
        font-size: $base-text-font-size;
        padding: $default-margin-padding-width 2rem;
      }

      &__subline {
        text-transform: uppercase;
      }

      h1 {
        margin-top: .75rem;
        overflow-wrap: break-word;
        word-break: auto-phrase;

        @include breakpoint-down($md) {
          font-size: $heading-h4-font-size;
        }
      }

      &__btn-container {
        display: inline-flex;
        gap: 0.75rem;
        justify-content: flex-end;
        flex-wrap: wrap;

        &__btn.k-button {
          margin-top: 2rem;

          .home-hero__text--corteco & {
            margin-top: 1rem;
          }

          .home-hero__text__btn-container--non-english & {
            margin-top: 1rem;
          }

          .k-button-solid-primary {
            color: $color__fst_gray__100;
          }

          @include breakpoint-down($between_md_lg) {
            margin-top: 1rem;
          }
        }

        &__text-link {
          position: absolute;
          bottom: -1rem;
          right: 1rem;
          color: $color__white;
          text-decoration: underline;
          font-weight: $base-text-font-weight-semi-bold;
          cursor: pointer;
          display: inline-flex;
          align-items: center;

          .home-hero__text__btn-container--non-english & {
            bottom: 0;
          }

          &:hover {
            text-decoration: none;
          }

          @include breakpoint-down($between_md_lg) {
            position: static;
            margin-top: 1rem;
            color: $color__primary;
          }
        }

        @include breakpoint-down($between_md_lg) {
            display: flex;
            justify-content: flex-end;
            padding-right: 2rem;
          }

          @include breakpoint-down($md) {
            padding-right: 0;
          }

          @include breakpoint-down($between_md_lg) {
            justify-content: flex-start;
          }
        }

      @include breakpoint-up($between_md_lg) {
        &--light {
          color: $color__white;

          h1 {
            color: $color__white;
          }
        }
      }
    }
  }
</style>
